/**
 * Renewals Page
 *
 * Main page for managing contract renewals with filtering and dashboard view.
 */

import { t, getCurrentLanguage, getCurrency } from '../../i18n/translations.js';
import { createRenewalCard, injectRenewalCardStyles } from '../../components/renewals/RenewalCard.js';
import { createRenewalTimeline, injectRenewalTimelineStyles } from '../../components/renewals/RenewalTimeline.js';
import { apiClient } from '../../services/api.js';
import { toast } from '../../services/toast.js';

/**
 * Format currency using user's preferred currency
 */
function formatCurrency(amount) {
  const lang = getCurrentLanguage();
  const currency = getCurrency();
  const locale = lang === 'ar' ? 'ar-SA' : lang === 'fr' ? 'fr-FR' : 'en-US';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/**
 * Renewals page state
 */
const state = {
  renewals: [],
  stats: null,
  loading: true,
  error: null,
  filters: {
    status: '',
    priority: '',
    action: '',
    daysAhead: 90,
  },
  selectedRenewal: null,
  actionModalRenewal: null,
  view: 'list', // 'list' | 'calendar'
};

/**
 * Fetch renewals from API
 */
async function fetchRenewals() {
  state.loading = true;
  state.error = null;

  try {
    const params = new URLSearchParams();
    if (state.filters.status) params.append('status', state.filters.status);
    if (state.filters.priority) params.append('priority', state.filters.priority);
    if (state.filters.action) params.append('action', state.filters.action);
    params.append('daysAhead', state.filters.daysAhead);

    const response = await apiClient.get(`/v1/renewals?${params.toString()}`);

    // Handle wrapped API response: { success, data: { items, pageInfo } } or { data: [...] }
    let items = [];
    if (response?.data?.data?.items) {
      items = response.data.data.items;
    } else if (response?.data?.items) {
      items = response.data.items;
    } else if (Array.isArray(response?.data?.data)) {
      items = response.data.data;
    } else if (Array.isArray(response?.data)) {
      items = response.data;
    } else if (Array.isArray(response)) {
      items = response;
    }

    state.renewals = items;
    state.stats = calculateStats(items);

  } catch (error) {
    console.error('Error fetching renewals:', error);
    state.error = error.message || 'Failed to load renewals';
    state.renewals = [];
    state.stats = calculateStats([]);
  } finally {
    state.loading = false;
    render();
  }
}

/**
 * Sync renewals from existing contracts
 */
async function syncRenewalsFromContracts() {
  state.loading = true;
  state.error = null;
  render(); // Show loading spinner

  try {
    const response = await apiClient.post('/v1/renewals/sync');
    const result = response?.data?.data || response?.data || response;

    console.log('Sync result:', result);

    // Refresh the renewals list after sync
    await fetchRenewals();

    // Show success message if any renewals were created
    if (result.created > 0) {
      console.log(`Created ${result.created} renewals from contracts`);
    }

  } catch (error) {
    console.error('Error syncing renewals:', error);
    state.error = error.message || 'Failed to sync renewals';
    state.loading = false;
    render(); // Re-render to show error state
  }
}

/**
 * Clean up duplicate renewals
 */
async function cleanupDuplicateRenewals() {
  state.loading = true;
  state.error = null;
  render(); // Show loading spinner

  try {
    const response = await apiClient.post('/v1/renewals/cleanup-duplicates');
    const result = response?.data?.data || response?.data || response;

    console.log('Cleanup result:', result);

    // Show success message
    if (result.message) {
      toast.success(result.message);
    }

    // Refresh the renewals list after cleanup
    await fetchRenewals();

  } catch (error) {
    console.error('Error cleaning up renewals:', error);
    state.error = error.message || 'Failed to cleanup renewals';
    state.loading = false;
    toast.error(t('renewal.cleanupError') || 'Failed to cleanup duplicates. Please try again.');
    render(); // Re-render to show error state
  }
}

/**
 * Calculate renewal statistics
 */
function calculateStats(renewals) {
  const stats = {
    total: renewals.length,
    critical: 0,
    urgent: 0,
    attention: 0,
    routine: 0,
    totalValueAtRisk: 0,
    currency: getCurrency(),
  };

  renewals.forEach((renewal) => {
    if (renewal.daysUntilRenewal < 14) {
      stats.critical++;
      stats.totalValueAtRisk += renewal.currentValue;
    } else if (renewal.daysUntilRenewal < 30) {
      stats.urgent++;
      stats.totalValueAtRisk += renewal.currentValue;
    } else if (renewal.daysUntilRenewal < 60) {
      stats.attention++;
    } else {
      stats.routine++;
    }
  });

  return stats;
}

/**
 * Navigate to renewal detail
 */
function viewRenewalDetail(renewal) {
  // In a real app, this would navigate to the detail page
  console.log('View renewal:', renewal.id);
  state.selectedRenewal = renewal;
  render();
}

/**
 * Open action modal for renewal
 */
function openActionModal(renewal) {
  console.log('Take action on renewal:', renewal.id);
  state.actionModalRenewal = renewal;
  renderActionModal();
}

/**
 * Close action modal
 */
function closeActionModal() {
  state.actionModalRenewal = null;
  const modal = document.getElementById('renewal-action-modal');
  if (modal) {
    modal.remove();
  }
}

/**
 * Update renewal action via API
 */
async function updateRenewalAction(renewalId, action) {
  try {
    const statusMap = {
      renew: 'action_taken',
      renegotiate: 'in_review',
      terminate: 'action_taken',
    };

    await apiClient.patch(`/v1/renewals/${renewalId}`, {
      action: action,
      status: statusMap[action] || 'in_review',
    });

    closeActionModal();
    await fetchRenewals();
  } catch (error) {
    console.error('Error updating renewal:', error);
    toast.error(t('renewal.updateError') || 'Failed to update renewal. Please try again.');
  }
}

/**
 * Render action modal
 */
function renderActionModal() {
  const renewal = state.actionModalRenewal;
  if (!renewal) return;

  // Remove existing modal if any
  const existingModal = document.getElementById('renewal-action-modal');
  if (existingModal) {
    existingModal.remove();
  }

  const modal = document.createElement('div');
  modal.id = 'renewal-action-modal';
  modal.className = 'modal-overlay';
  modal.innerHTML = `
    <div class="modal-content action-modal">
      <div class="modal-header">
        <h2>${t('renewal.takeAction')}</h2>
        <button class="btn btn--icon modal-close" id="close-action-modal">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
      <div class="modal-body">
        <div class="action-modal__contract-info">
          <h3>${renewal.contractTitle || renewal.contractNumber}</h3>
          <p>${renewal.vendorName}</p>
          <p class="action-modal__meta">
            ${t('renewal.renewalDate')}: <strong>${new Date(renewal.renewalDate).toLocaleDateString()}</strong>
            &nbsp;•&nbsp;
            ${renewal.daysUntilRenewal} ${t('renewal.daysLeft')}
          </p>
        </div>

        <div class="action-modal__options">
          <p class="action-modal__prompt">${t('renewal.selectAction') || 'What would you like to do with this contract?'}</p>

          <button class="action-option action-option--renew" data-action="renew">
            <div class="action-option__icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <div class="action-option__content">
              <strong>${t('renewal.action.renew')}</strong>
              <span>${t('renewal.action.renewDesc') || 'Continue the contract with current terms'}</span>
            </div>
          </button>

          <button class="action-option action-option--renegotiate" data-action="renegotiate">
            <div class="action-option__icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 20h9"/>
                <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
              </svg>
            </div>
            <div class="action-option__content">
              <strong>${t('renewal.action.renegotiate')}</strong>
              <span>${t('renewal.action.renegotiateDesc') || 'Request new terms or pricing'}</span>
            </div>
          </button>

          <button class="action-option action-option--terminate" data-action="terminate">
            <div class="action-option__icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/>
                <line x1="15" y1="9" x2="9" y2="15"/>
                <line x1="9" y1="9" x2="15" y2="15"/>
              </svg>
            </div>
            <div class="action-option__content">
              <strong>${t('renewal.action.terminate')}</strong>
              <span>${t('renewal.action.terminateDesc') || 'Do not renew this contract'}</span>
            </div>
          </button>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  // Event listeners
  modal.querySelector('#close-action-modal').addEventListener('click', closeActionModal);
  modal.addEventListener('click', (e) => {
    if (e.target === modal) closeActionModal();
  });

  modal.querySelectorAll('.action-option').forEach((btn) => {
    btn.addEventListener('click', () => {
      const action = btn.dataset.action;
      updateRenewalAction(renewal.id, action);
    });
  });

  // Inject modal styles if not already present
  injectActionModalStyles();
}

/**
 * Inject action modal styles
 */
function injectActionModalStyles() {
  if (document.getElementById('action-modal-styles')) return;

  const styles = document.createElement('style');
  styles.id = 'action-modal-styles';
  styles.textContent = `
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      animation: fadeIn 0.2s ease;
    }

    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }

    .modal-content {
      background: var(--surface);
      border-radius: var(--radius-lg);
      box-shadow: var(--shadow-xl);
      max-width: 500px;
      width: 90%;
      max-height: 90vh;
      overflow: auto;
      animation: slideUp 0.3s ease;
    }

    @keyframes slideUp {
      from { transform: translateY(20px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: var(--space-4);
      border-bottom: 1px solid var(--border);
    }

    .modal-header h2 {
      margin: 0;
      font-size: var(--text-lg);
    }

    .modal-close {
      background: none;
      border: none;
      cursor: pointer;
      padding: var(--space-1);
      color: var(--text-secondary);
    }

    .modal-close:hover {
      color: var(--text-primary);
    }

    .modal-body {
      padding: var(--space-4);
    }

    .action-modal__contract-info {
      text-align: center;
      margin-bottom: var(--space-4);
      padding-bottom: var(--space-4);
      border-bottom: 1px solid var(--border);
    }

    .action-modal__contract-info h3 {
      margin: 0 0 var(--space-1);
      font-size: var(--text-lg);
    }

    .action-modal__contract-info p {
      margin: 0;
      color: var(--text-secondary);
    }

    .action-modal__meta {
      margin-top: var(--space-2) !important;
      font-size: var(--text-sm);
    }

    .action-modal__prompt {
      text-align: center;
      margin-bottom: var(--space-4);
      color: var(--text-secondary);
    }

    .action-modal__options {
      display: flex;
      flex-direction: column;
      gap: var(--space-3);
    }

    .action-option {
      display: flex;
      align-items: center;
      gap: var(--space-3);
      padding: var(--space-4);
      background: var(--background);
      border: 2px solid var(--border);
      border-radius: var(--radius-lg);
      cursor: pointer;
      transition: all 0.2s;
      text-align: left;
      width: 100%;
    }

    .action-option:hover {
      border-color: var(--primary);
      box-shadow: var(--shadow-sm);
    }

    .action-option__icon {
      width: 48px;
      height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: var(--radius);
      flex-shrink: 0;
    }

    .action-option__icon svg {
      width: 24px;
      height: 24px;
    }

    .action-option--renew .action-option__icon {
      background: #dcfce7;
      color: #16a34a;
    }

    .action-option--renegotiate .action-option__icon {
      background: #fef3c7;
      color: #d97706;
    }

    .action-option--terminate .action-option__icon {
      background: #fee2e2;
      color: #dc2626;
    }

    .action-option__content {
      flex: 1;
    }

    .action-option__content strong {
      display: block;
      margin-bottom: var(--space-1);
      font-size: var(--text-base);
    }

    .action-option__content span {
      font-size: var(--text-sm);
      color: var(--text-secondary);
    }

    .action-option--renew:hover {
      border-color: #16a34a;
      background: #f0fdf4;
    }

    .action-option--renegotiate:hover {
      border-color: #d97706;
      background: #fffbeb;
    }

    .action-option--terminate:hover {
      border-color: #dc2626;
      background: #fef2f2;
    }
  `;

  document.head.appendChild(styles);
}

/**
 * Update filter and refresh
 */
function updateFilter(key, value) {
  state.filters[key] = value;
  fetchRenewals().then(render);
}

/**
 * Render the stats cards
 */
function renderStats(container) {
  if (!state.stats) return;

  container.innerHTML = `
    <div class="renewals-stats">
      <div class="stat-card stat-card--critical">
        <div class="stat-card__icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <line x1="12" y1="9" x2="12" y2="13"/>
            <line x1="12" y1="17" x2="12.01" y2="17"/>
          </svg>
        </div>
        <div class="stat-card__content">
          <div class="stat-card__value">${state.stats.critical}</div>
          <div class="stat-card__label">${t('renewal.stats.critical')}</div>
          <div class="stat-card__sublabel">&lt; 14 ${t('renewal.days')}</div>
        </div>
      </div>

      <div class="stat-card stat-card--urgent">
        <div class="stat-card__icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
        </div>
        <div class="stat-card__content">
          <div class="stat-card__value">${state.stats.urgent}</div>
          <div class="stat-card__label">${t('renewal.stats.urgent')}</div>
          <div class="stat-card__sublabel">14-30 ${t('renewal.days')}</div>
        </div>
      </div>

      <div class="stat-card stat-card--attention">
        <div class="stat-card__icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
        </div>
        <div class="stat-card__content">
          <div class="stat-card__value">${state.stats.attention}</div>
          <div class="stat-card__label">${t('renewal.stats.attention')}</div>
          <div class="stat-card__sublabel">30-60 ${t('renewal.days')}</div>
        </div>
      </div>

      <div class="stat-card stat-card--value">
        <div class="stat-card__icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="1" x2="12" y2="23"/>
            <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
          </svg>
        </div>
        <div class="stat-card__content">
          <div class="stat-card__value">${formatCurrency(state.stats.totalValueAtRisk, state.stats.currency)}</div>
          <div class="stat-card__label">${t('renewal.stats.valueAtRisk')}</div>
          <div class="stat-card__sublabel">${t('renewal.stats.needsDecision')}</div>
        </div>
      </div>
    </div>
  `;
}

/**
 * Render the filters
 */
function renderFilters(container) {
  container.innerHTML = `
    <div class="renewals-filters">
      <div class="renewals-filters__group">
        <label class="renewals-filters__label">${t('renewal.filter.status')}</label>
        <select class="renewals-filters__select" id="filter-status">
          <option value="">${t('renewal.filter.allStatuses')}</option>
          <option value="upcoming" ${state.filters.status === 'upcoming' ? 'selected' : ''}>${t('renewal.status.upcoming')}</option>
          <option value="in_review" ${state.filters.status === 'in_review' ? 'selected' : ''}>${t('renewal.status.in_review')}</option>
          <option value="action_taken" ${state.filters.status === 'action_taken' ? 'selected' : ''}>${t('renewal.status.action_taken')}</option>
          <option value="expired" ${state.filters.status === 'expired' ? 'selected' : ''}>${t('renewal.status.expired')}</option>
        </select>
      </div>

      <div class="renewals-filters__group">
        <label class="renewals-filters__label">${t('renewal.filter.priority')}</label>
        <select class="renewals-filters__select" id="filter-priority">
          <option value="">${t('renewal.filter.allPriorities')}</option>
          <option value="critical" ${state.filters.priority === 'critical' ? 'selected' : ''}>${t('renewal.priority.critical')}</option>
          <option value="high" ${state.filters.priority === 'high' ? 'selected' : ''}>${t('renewal.priority.high')}</option>
          <option value="medium" ${state.filters.priority === 'medium' ? 'selected' : ''}>${t('renewal.priority.medium')}</option>
          <option value="low" ${state.filters.priority === 'low' ? 'selected' : ''}>${t('renewal.priority.low')}</option>
        </select>
      </div>

      <div class="renewals-filters__group">
        <label class="renewals-filters__label">${t('renewal.filter.action')}</label>
        <select class="renewals-filters__select" id="filter-action">
          <option value="">${t('renewal.filter.allActions')}</option>
          <option value="undecided" ${state.filters.action === 'undecided' ? 'selected' : ''}>${t('renewal.action.undecided')}</option>
          <option value="renew" ${state.filters.action === 'renew' ? 'selected' : ''}>${t('renewal.action.renew')}</option>
          <option value="renegotiate" ${state.filters.action === 'renegotiate' ? 'selected' : ''}>${t('renewal.action.renegotiate')}</option>
          <option value="terminate" ${state.filters.action === 'terminate' ? 'selected' : ''}>${t('renewal.action.terminate')}</option>
        </select>
      </div>

      <div class="renewals-filters__group">
        <label class="renewals-filters__label">${t('renewal.filter.timeframe')}</label>
        <select class="renewals-filters__select" id="filter-days">
          <option value="30" ${state.filters.daysAhead === 30 ? 'selected' : ''}>30 ${t('renewal.days')}</option>
          <option value="60" ${state.filters.daysAhead === 60 ? 'selected' : ''}>60 ${t('renewal.days')}</option>
          <option value="90" ${state.filters.daysAhead === 90 ? 'selected' : ''}>90 ${t('renewal.days')}</option>
          <option value="180" ${state.filters.daysAhead === 180 ? 'selected' : ''}>180 ${t('renewal.days')}</option>
        </select>
      </div>

      <div class="renewals-filters__actions">
        <button class="btn btn--secondary btn--sm" id="cleanup-duplicates" title="${t('renewal.cleanupDuplicates') || 'Remove duplicate renewals'}">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16">
            <polyline points="3 6 5 6 21 6"/>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
          </svg>
          ${t('renewal.cleanup') || 'Cleanup'}
        </button>
        <button class="btn btn--secondary" id="view-toggle">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            ${state.view === 'list'
              ? '<rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>'
              : '<line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>'
            }
          </svg>
          ${state.view === 'list' ? t('renewal.view.calendar') : t('renewal.view.list')}
        </button>
      </div>
    </div>
  `;

  // Add event listeners
  container.querySelector('#filter-status').addEventListener('change', (e) => updateFilter('status', e.target.value));
  container.querySelector('#filter-priority').addEventListener('change', (e) => updateFilter('priority', e.target.value));
  container.querySelector('#filter-action').addEventListener('change', (e) => updateFilter('action', e.target.value));
  container.querySelector('#filter-days').addEventListener('change', (e) => updateFilter('daysAhead', parseInt(e.target.value)));
  container.querySelector('#cleanup-duplicates').addEventListener('click', cleanupDuplicateRenewals);
  container.querySelector('#view-toggle').addEventListener('click', () => {
    state.view = state.view === 'list' ? 'calendar' : 'list';
    render();
  });
}

/**
 * Render the renewals list
 */
function renderList(container) {
  if (state.loading) {
    container.innerHTML = `
      <div class="page-loading">
        <div class="loading-spinner">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="spinner-icon">
            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
          </svg>
          <p>${t('common.loading')}</p>
        </div>
      </div>
    `;
    return;
  }

  if (state.error) {
    container.innerHTML = `
      <div class="renewals-error">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <line x1="15" y1="9" x2="9" y2="15"/>
          <line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
        <p>${state.error}</p>
        <button class="btn btn--primary" onclick="location.reload()">
          ${t('common.retry')}
        </button>
      </div>
    `;
    return;
  }

  if (state.renewals.length === 0) {
    container.innerHTML = `
      <div class="renewals-empty">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
        <p>${t('renewal.empty.title') || 'No Upcoming Renewals'}</p>
        <span>${t('renewal.empty.description') || 'All your contracts are up to date. Upload contracts with expiration dates to track renewals.'}</span>
        <a href="/#/contracts" class="btn btn--secondary" style="margin-top: 1rem;">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="18" height="18">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
            <polyline points="14 2 14 8 20 8"/>
          </svg>
          ${t('renewal.viewContracts') || 'View Contracts'}
        </a>
      </div>
    `;
    return;
  }

  container.innerHTML = '';
  
  const grid = document.createElement('div');
  grid.className = 'renewals-grid';

  state.renewals.forEach((renewal) => {
    const card = createRenewalCard(renewal, {
      onView: viewRenewalDetail,
      onAction: openActionModal,
    });
    grid.appendChild(card);
  });

  container.appendChild(grid);
}

/**
 * Render the detail panel
 */
function renderDetailPanel(container) {
  if (!state.selectedRenewal) {
    container.innerHTML = '';
    container.style.display = 'none';
    return;
  }

  container.style.display = 'block';
  container.innerHTML = `
    <div class="renewal-detail-panel">
      <div class="renewal-detail-panel__header">
        <h2>${state.selectedRenewal.contractTitle}</h2>
        <button class="btn btn--icon" id="close-detail">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
      <div class="renewal-detail-panel__content" id="detail-content"></div>
    </div>
  `;

  // Render timeline
  const detailContent = container.querySelector('#detail-content');
  const timeline = createRenewalTimeline(state.selectedRenewal);
  detailContent.appendChild(timeline);

  // Close button
  container.querySelector('#close-detail').addEventListener('click', () => {
    state.selectedRenewal = null;
    render();
  });
}

/**
 * Main render function
 */
function render() {
  const root = document.getElementById('renewals-page') || document.getElementById('app-content');
  if (!root) return;

  // Show full-page loading spinner while data is loading
  if (state.loading) {
    root.innerHTML = `
      <div class="page-loading">
        <div class="loading-spinner">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="spinner-icon">
            <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
          </svg>
          <p>${t('common.loading') || 'Loading...'}</p>
        </div>
      </div>
    `;
    return;
  }

  root.innerHTML = `
    <div class="renewals-page">
      <header class="page-header">
        <div class="page-header__content">
          <h1 class="page-header__title">${t('renewal.pageTitle')}</h1>
          <p class="page-header__subtitle">${t('renewal.pageSubtitle')}</p>
        </div>
      </header>

      <div id="renewals-stats"></div>
      <div id="renewals-filters"></div>

      <div class="renewals-main">
        <div id="renewals-list" class="renewals-list-container"></div>
        <div id="renewals-detail" class="renewals-detail-container"></div>
      </div>
    </div>
  `;

  // Inject styles
  injectRenewalCardStyles();
  injectRenewalTimelineStyles();
  injectPageStyles();

  // Render sections
  renderStats(root.querySelector('#renewals-stats'));
  renderFilters(root.querySelector('#renewals-filters'));
  renderList(root.querySelector('#renewals-list'));
  renderDetailPanel(root.querySelector('#renewals-detail'));
}

/**
 * Inject page-specific styles
 */
function injectPageStyles() {
  if (document.getElementById('renewals-page-styles')) return;

  const styles = document.createElement('style');
  styles.id = 'renewals-page-styles';
  styles.textContent = `
    .renewals-page {
      max-width: 1400px;
      margin: 0 auto;
      padding: var(--space-4);
    }

    .page-header {
      margin-bottom: var(--space-6);
    }

    .page-header__title {
      font-size: var(--text-2xl);
      font-weight: 700;
      margin: 0 0 var(--space-1);
    }

    .page-header__subtitle {
      color: var(--text-secondary);
      margin: 0;
    }

    .renewals-stats {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
      gap: var(--space-4);
      margin-bottom: var(--space-6);
    }

    .stat-card {
      display: flex;
      align-items: center;
      gap: var(--space-3);
      padding: var(--space-4);
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: var(--radius-lg);
      border-left: 4px solid var(--primary);
    }

    .stat-card--critical { border-left-color: #dc2626; }
    .stat-card--urgent { border-left-color: #ea580c; }
    .stat-card--attention { border-left-color: #ca8a04; }
    .stat-card--value { border-left-color: var(--primary); }

    .stat-card__icon {
      width: 48px;
      height: 48px;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: var(--radius);
      background: var(--background);
    }

    .stat-card__icon svg {
      width: 24px;
      height: 24px;
      color: var(--text-secondary);
    }

    .stat-card--critical .stat-card__icon { background: #fef2f2; }
    .stat-card--critical .stat-card__icon svg { color: #dc2626; }
    .stat-card--urgent .stat-card__icon { background: #fff7ed; }
    .stat-card--urgent .stat-card__icon svg { color: #ea580c; }
    .stat-card--attention .stat-card__icon { background: #fefce8; }
    .stat-card--attention .stat-card__icon svg { color: #ca8a04; }

    .stat-card__value {
      font-size: var(--text-2xl);
      font-weight: 700;
    }

    .stat-card__label {
      font-weight: 500;
    }

    .stat-card__sublabel {
      font-size: var(--text-xs);
      color: var(--text-secondary);
    }

    .renewals-filters {
      display: flex;
      flex-wrap: wrap;
      gap: var(--space-4);
      align-items: flex-end;
      padding: var(--space-4);
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: var(--radius-lg);
      margin-bottom: var(--space-4);
    }

    .renewals-filters__group {
      display: flex;
      flex-direction: column;
      gap: var(--space-1);
    }

    .renewals-filters__label {
      font-size: var(--text-xs);
      font-weight: 500;
      color: var(--text-secondary);
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }

    .renewals-filters__select {
      padding: var(--space-2) var(--space-3);
      border: 1px solid var(--border);
      border-radius: var(--radius);
      background: var(--background);
      font-size: var(--text-sm);
      min-width: 150px;
    }

    .renewals-filters__actions {
      margin-left: auto;
    }

    .renewals-main {
      display: flex;
      gap: var(--space-4);
    }

    .renewals-list-container {
      flex: 1;
      min-width: 0;
    }

    .renewals-detail-container {
      width: 400px;
      flex-shrink: 0;
      display: none;
    }

    .renewals-grid {
      display: grid;
      gap: var(--space-4);
    }

    .renewals-loading,
    .renewals-error,
    .renewals-empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: var(--space-8);
      text-align: center;
      color: var(--text-secondary);
    }

    .renewals-loading svg,
    .renewals-error svg,
    .renewals-empty svg {
      width: 48px;
      height: 48px;
      margin-bottom: var(--space-4);
      opacity: 0.5;
    }

    .renewal-detail-panel {
      position: sticky;
      top: var(--space-4);
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: var(--radius-lg);
      overflow: hidden;
    }

    .renewal-detail-panel__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: var(--space-4);
      border-bottom: 1px solid var(--border);
    }

    .renewal-detail-panel__header h2 {
      font-size: var(--text-lg);
      font-weight: 600;
      margin: 0;
    }

    .renewal-detail-panel__content {
      padding: var(--space-4);
      max-height: calc(100vh - 200px);
      overflow-y: auto;
    }

    @media (max-width: 1024px) {
      .renewals-main {
        flex-direction: column;
      }

      .renewals-detail-container {
        width: 100%;
      }
    }

    @media (max-width: 768px) {
      .renewals-stats {
        grid-template-columns: repeat(2, 1fr);
      }

      .renewals-filters {
        flex-direction: column;
        align-items: stretch;
      }

      .renewals-filters__group {
        width: 100%;
      }

      .renewals-filters__select {
        width: 100%;
      }

      .renewals-filters__actions {
        margin-left: 0;
      }
    }
  `;

  document.head.appendChild(styles);
}

/**
 * Initialize the renewals page
 */
export async function init() {
  // Render first to show loading spinner
  render();

  // Fetch renewals first
  await fetchRenewals();

  // If no renewals found, automatically sync from contracts
  if (state.renewals.length === 0 && !state.error) {
    console.log('No renewals found, automatically syncing from contracts...');
    await syncRenewalsFromContracts();
  }
}

/**
 * Cleanup function
 */
export function destroy() {
  // Cleanup if needed
}

export default { init, destroy };
